import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import { IconClock, IconPencil, IconPin, IconPinFilled, IconTrash } from "@tabler/icons-react";
import { ROLES } from "@wwrp/shared";
import type { Bulletin, CreateBulletinBody, PatchBulletinBody, Role } from "@wwrp/shared";
import { bulletinsApi } from "../api/bulletins.js";
import { MarkdownContent } from "./MarkdownContent.js";

// ── Shared colour / label maps ────────────────────────────────────────────────

export const SEVERITY_COLOR: Record<string, string> = {
  info: "blue",
  success: "teal",
  warning: "orange",
  danger: "red",
};

export const SEVERITY_LABEL: Record<string, string> = {
  info: "Info",
  success: "Update",
  warning: "Warning",
  danger: "Urgent",
};

export const ROLE_SHORT_LABEL: Record<string, string> = {
  applicant: "All",
  recruit: "Recruit+",
  member: "Member+",
  staff_in_training: "SiT+",
  staff: "Staff+",
  senior_staff: "Sr. Staff+",
  junior_administration: "JA+",
  administration: "Admin+",
  senior_administration: "Sr. Admin+",
  leadership: "Leadership+",
  head_administration: "Head Admin+",
  development_command: "Dev Command+",
};

export const ROLE_VISIBILITY_OPTIONS = ROLES.map((r) => ({
  value: r,
  label: ROLE_SHORT_LABEL[r] ?? r,
}));

/** Returns a human-readable relative time string for an ISO timestamp. */
export function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const abs = Math.abs(diff);
  const future = diff < 0;
  const pfx = future ? "in " : "";
  const sfx = future ? "" : " ago";

  if (abs < 60_000) return "just now";
  if (abs < 3_600_000) {
    const m = Math.round(abs / 60_000);
    return `${pfx}${m}m${sfx}`;
  }
  if (abs < 86_400_000) {
    const h = Math.round(abs / 3_600_000);
    return `${pfx}${h}h${sfx}`;
  }
  const d = Math.round(abs / 86_400_000);
  return `${pfx}${d}d${sfx}`;
}

// ── BulletinCard ──────────────────────────────────────────────────────────────

export function BulletinCard({
  bulletin: b,
  isAdmin,
  expired = false,
  onEdit,
  onDelete,
}: {
  bulletin: Bulletin;
  isAdmin: boolean;
  expired?: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const color = SEVERITY_COLOR[b.severity] ?? "blue";

  const expiryLabel = b.expiresAt
    ? expired
      ? `Expired ${formatRelative(b.expiresAt)}`
      : `Expires ${formatRelative(b.expiresAt)}`
    : null;

  return (
    <Card
      withBorder
      radius="md"
      p="sm"
      style={{
        borderLeft: `3px solid var(--mantine-color-${color}-6)`,
        opacity: expired ? 0.55 : 1,
      }}
    >
      <Group justify="space-between" wrap="nowrap" align="flex-start" gap="xs">
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs" wrap="nowrap">
            {b.isPinned && (
              <IconPinFilled
                size={13}
                style={{ color: `var(--mantine-color-${color}-5)`, flexShrink: 0 }}
              />
            )}
            <Text fw={700} size="sm" style={{ lineHeight: 1.3 }}>
              {b.title}
            </Text>
            <Badge size="xs" variant="light" color={color} style={{ flexShrink: 0 }}>
              {SEVERITY_LABEL[b.severity] ?? b.severity}
            </Badge>
          </Group>

          <MarkdownContent value={b.body} size="sm" />

          <Group gap="xs" mt={2}>
            {b.authorName && (
              <Text size="xs" c="dimmed">
                {b.authorName}
              </Text>
            )}
            <Text size="xs" c="dimmed">
              ·
            </Text>
            <Text size="xs" c="dimmed">
              {formatRelative(b.createdAt)}
            </Text>
            {expiryLabel && (
              <>
                <Text size="xs" c="dimmed">
                  ·
                </Text>
                <Group gap={4}>
                  <IconClock size={11} style={{ color: "var(--mantine-color-dimmed)" }} />
                  <Text size="xs" c={expired ? "red" : "dimmed"}>
                    {expiryLabel}
                  </Text>
                </Group>
              </>
            )}
          </Group>
        </Stack>

        <Stack gap={4} align="flex-end" style={{ flexShrink: 0 }}>
          {b.minRole !== "applicant" && (
            <Badge size="xs" variant="dot" color="gray" style={{ whiteSpace: "nowrap" }}>
              {ROLE_SHORT_LABEL[b.minRole] ?? `${b.minRole}+`}
            </Badge>
          )}
          {isAdmin && (
            <Group gap={4}>
              <Button
                size="compact-xs"
                variant="subtle"
                color="gray"
                onClick={onEdit}
                title="Edit bulletin"
              >
                <IconPencil size={13} />
              </Button>
              <Button
                size="compact-xs"
                variant="subtle"
                color="red"
                onClick={onDelete}
                title="Delete bulletin"
              >
                <IconTrash size={13} />
              </Button>
            </Group>
          )}
        </Stack>
      </Group>
    </Card>
  );
}

// ── BulletinFormModal ─────────────────────────────────────────────────────────

export function BulletinFormModal({
  existing,
  onClose,
  onSuccess,
}: {
  existing?: Bulletin;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const isEdit = !!existing;
  const [title, setTitle] = useState(existing?.title ?? "");
  const [body, setBody] = useState(existing?.body ?? "");
  const [severity, setSeverity] = useState<string>(existing?.severity ?? "info");
  const [isPinned, setIsPinned] = useState(existing?.isPinned ?? false);
  const [minRole, setMinRole] = useState<Role>(existing?.minRole ?? "applicant");
  // DateTimePicker.onChange delivers an ISO string in this Mantine build.
  const [expiresAt, setExpiresAt] = useState<string | null>(existing?.expiresAt ?? null);
  const [error, setError] = useState("");

  const mutation = useMutation({
    mutationFn: () => {
      const expiresAtIso = expiresAt ?? null;
      if (isEdit && existing) {
        const patch: PatchBulletinBody = {
          title: title.trim(),
          body: body.trim(),
          severity: severity as CreateBulletinBody["severity"],
          isPinned,
          minRole,
          expiresAt: expiresAtIso,
        };
        return bulletinsApi.update(existing.id, patch);
      }
      const create: CreateBulletinBody = {
        title: title.trim(),
        body: body.trim(),
        severity: severity as CreateBulletinBody["severity"],
        isPinned,
        minRole,
        expiresAt: expiresAtIso,
      };
      return bulletinsApi.create(create);
    },
    onSuccess: () => {
      notifications.show({
        message: isEdit ? "Bulletin updated." : "Bulletin posted.",
        color: "teal",
      });
      onSuccess();
    },
    onError: (e: Error) => setError(e.message),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (!title.trim()) {
      setError("Title is required.");
      return;
    }
    if (!body.trim()) {
      setError("Body is required.");
      return;
    }
    mutation.mutate();
  }

  return (
    <Modal
      opened
      onClose={onClose}
      title={isEdit ? "Edit Bulletin" : "New Community Bulletin"}
      size="lg"
      centered
      radius="md"
    >
      <form onSubmit={submit}>
        <Stack gap="md">
          {error && (
            <Text size="sm" c="red">
              {error}
            </Text>
          )}

          <TextInput
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
            placeholder="What's the key message?"
            required
          />

          <Textarea
            label="Body"
            description="Supports Markdown (lists, bold, links)."
            value={body}
            onChange={(e) => setBody(e.currentTarget.value)}
            autosize
            minRows={4}
            maxRows={16}
            placeholder="Write your bulletin here…"
            required
          />

          <Group grow>
            <Select
              label="Severity"
              value={severity}
              onChange={(v) => setSeverity(v ?? "info")}
              data={[
                { value: "info", label: "Info (blue)" },
                { value: "success", label: "Update (teal)" },
                { value: "warning", label: "Warning (orange)" },
                { value: "danger", label: "Urgent (red)" },
              ]}
            />

            <Select
              label="Visible to"
              description="Who can see this bulletin."
              value={minRole}
              onChange={(v) => setMinRole((v ?? "applicant") as Role)}
              data={ROLE_VISIBILITY_OPTIONS}
            />
          </Group>

          <DateTimePicker
            label="Expires at"
            description="Leave blank to never expire."
            value={expiresAt ? new Date(expiresAt) : null}
            onChange={(v) => setExpiresAt(v ? (v as unknown as string) : null)}
            clearable
            placeholder="No expiry"
            minDate={new Date()}
          />

          <Switch
            label="Pin to top"
            description="Pinned bulletins always appear first regardless of age."
            checked={isPinned}
            onChange={(e) => setIsPinned(e.currentTarget.checked)}
            color="blue"
          />

          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              color={SEVERITY_COLOR[severity] ?? "blue"}
              loading={mutation.isPending}
              leftSection={isPinned ? <IconPin size={14} /> : undefined}
            >
              {mutation.isPending ? "Saving…" : isEdit ? "Save Changes" : "Post Bulletin"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
